/**
 * Daily Self-Assessment Schedule — Operating day calculator.
 *
 * Maps calendar dates to operating days (skipping weekends + holidays).
 * Used by the rotation engine to determine which facility area to assess each day.
 */

// US federal holidays (month-day format for recurring, full date for floating)
// Floating holidays (MLK, Presidents, Memorial, Labor, Thanksgiving) need year-specific calculation
const FIXED_HOLIDAYS = [
  '01-01', // New Year's Day
  '07-04', // Independence Day
  '12-25', // Christmas Day
]

function getFloatingHolidays(year: number): string[] {
  const holidays: string[] = []

  // MLK Day — 3rd Monday of January
  holidays.push(getNthWeekday(year, 0, 1, 3))
  // Presidents Day — 3rd Monday of February
  holidays.push(getNthWeekday(year, 1, 1, 3))
  // Memorial Day — last Monday of May
  holidays.push(getLastWeekday(year, 4, 1))
  // Labor Day — 1st Monday of September
  holidays.push(getNthWeekday(year, 8, 1, 1))
  // Thanksgiving — 4th Thursday of November
  holidays.push(getNthWeekday(year, 10, 4, 4))
  // Day after Thanksgiving
  const thanksgiving = getNthWeekday(year, 10, 4, 4)
  const tDate = new Date(`${year}-${thanksgiving}`)
  tDate.setDate(tDate.getDate() + 1)
  holidays.push(formatMMDD(tDate))

  return holidays
}

function getNthWeekday(year: number, month: number, weekday: number, n: number): string {
  let count = 0
  for (let day = 1; day <= 31; day++) {
    const d = new Date(year, month, day)
    if (d.getMonth() !== month) break
    if (d.getDay() === weekday) {
      count++
      if (count === n) return formatMMDD(d)
    }
  }
  return '01-01' // fallback
}

function getLastWeekday(year: number, month: number, weekday: number): string {
  let last: Date | null = null
  for (let day = 1; day <= 31; day++) {
    const d = new Date(year, month, day)
    if (d.getMonth() !== month) break
    if (d.getDay() === weekday) last = d
  }
  return last ? formatMMDD(last) : '01-01'
}

function formatMMDD(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${mm}-${dd}`
}

/**
 * Check if a date is a non-operating day (weekend or holiday).
 */
export function isNonOperatingDay(date: Date, customHolidays: string[] = []): boolean {
  const day = date.getDay()
  if (day === 0 || day === 6) return true // Weekend

  const mmdd = formatMMDD(date)
  if (FIXED_HOLIDAYS.includes(mmdd)) return true
  if (customHolidays.includes(mmdd)) return true

  const floating = getFloatingHolidays(date.getFullYear())
  if (floating.includes(mmdd)) return true

  return false
}

/**
 * Get the operating day number for a given date within the current year.
 * Operating days skip weekends and holidays, starting from 0 on the first
 * operating day of the year.
 *
 * Returns -1 if the given date is a non-operating day.
 */
export function getOperatingDayNumber(date: Date, customHolidays: string[] = []): number {
  if (isNonOperatingDay(date, customHolidays)) return -1

  const year = date.getFullYear()
  const startOfYear = new Date(year, 0, 1)
  let operatingDay = 0

  for (let d = new Date(startOfYear); d < date; d.setDate(d.getDate() + 1)) {
    if (!isNonOperatingDay(d, customHolidays)) {
      operatingDay++
    }
  }

  return operatingDay
}
